import { AuthApiErrorResponse } from './AuthApiErrorResponse';
import { AuthApiResponse, AuthApiSchema } from './AuthApiSchema';

/**
 * Options for a single Auth API request.
 */
export interface AuthApiRequestOptions {
    /** Signal that aborts the request, e.g. on a timeout. */
    signal: AbortSignal;
}

/**
 * Configuration for the Auth API client.
 */
export interface AuthApiClientConfiguration {
    /** Base url of the Auth API for the current country, without a trailing slash. */
    baseUrl: string;

    /** The Accept-Language header value, e.g. 'sv-SE'. */
    acceptLanguage: string;
}

/**
 * A minimal typed client for the Auth API endpoints the auth client uses.
 *
 * The Next.js site drives the same endpoints through `openapi-fetch`; this
 * repository is being retired, so the requests are written out by hand instead
 * of taking on a new dependency — typed against the same generated schema
 * (`api.generated.ts`).
 *
 * Only the session-discovery endpoints exist here: signing in happens on the
 * Next.js site, so the sign-in endpoints from the source were never ported.
 * All three requests are bodiless.
 *
 * The session cookie is sent with `credentials: 'include'`, and keeping the
 * transport on plain `fetch` keeps the auth client class portable.
 */
export class AuthApiClient {
    constructor(private readonly configuration: AuthApiClientConfiguration) {}

    getSessionInfo(options: AuthApiRequestOptions): Promise<AuthApiResponse<'GetSessionInfoEndpoint'>> {
        return this.request('GET', '/session', options) as Promise<AuthApiResponse<'GetSessionInfoEndpoint'>>;
    }

    createAccessToken(options: AuthApiRequestOptions): Promise<AuthApiSchema['AccessTokenDto']> {
        return this.request('POST', '/session/access-token', options) as Promise<AuthApiSchema['AccessTokenDto']>;
    }

    async destroySession(options: AuthApiRequestOptions): Promise<void> {
        await this.request('DELETE', '/session', options);
    }

    /**
     * Sends one request, throwing an AuthApiErrorResponse for non-2xx answers.
     * @param method The HTTP method.
     * @param path The path relative to the Auth API base url.
     * @param options The per-request options.
     * @returns The decoded JSON body, or undefined for answers without one.
     */
    private async request(method: string, path: string, options: AuthApiRequestOptions): Promise<unknown> {
        const headers = new Headers();
        headers.set('Accept-Language', this.configuration.acceptLanguage);

        const response = await fetch(this.configuration.baseUrl + path, {
            method,
            headers,
            credentials: 'include',
            signal: options.signal,
        });

        if (!response.ok) {
            throw await AuthApiErrorResponse.fromResponse(response);
        }

        // 204 answers carry no body; the public methods type that as void.
        if (response.status === 204) {
            return undefined;
        }

        return response.json();
    }
}
